import { useEffect } from "react";
import { siteConfig } from "../config";

/** Set document <title>, meta description, canonical URL, OG/Twitter cards,
 *  and inject JSON-LD blocks. Safe to call from any page. */
export default function Seo({
  title,
  description,
  canonicalPath = "",
  jsonLd = [],
  ogImage,
  ogType = "website",
}) {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (name, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[name="${name}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("name", name);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    const setProp = (property, content) => {
      if (!content) return;
      let m = document.querySelector(`meta[property="${property}"]`);
      if (!m) {
        m = document.createElement("meta");
        m.setAttribute("property", property);
        document.head.appendChild(m);
      }
      m.setAttribute("content", content);
    };
    setMeta("description", description);

    // Canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    const canonicalHref = `${siteConfig.links.siteDomain}${canonicalPath || window.location.pathname}`;
    link.setAttribute("href", canonicalHref);

    // Per-page Open Graph + Twitter cards (override the static ones in index.html)
    const img = ogImage || `${siteConfig.links.siteDomain}/og-cover.jpg`;
    setProp("og:type", ogType);
    setProp("og:url", canonicalHref);
    if (title) setProp("og:title", title);
    if (description) setProp("og:description", description);
    setProp("og:image", img);
    setProp("og:site_name", siteConfig.brand.name);
    setProp("og:locale", "en_US");

    setMeta("twitter:card", "summary_large_image");
    if (title) setMeta("twitter:title", title);
    if (description) setMeta("twitter:description", description);
    setMeta("twitter:image", img);

    // Inject JSON-LD
    document.querySelectorAll('script[data-ssc-jsonld="true"]').forEach((s) => s.remove());
    const blocks = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
    blocks.forEach((b) => {
      if (!b) return;
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-ssc-jsonld", "true");
      s.text = JSON.stringify(b);
      document.head.appendChild(s);
    });

    window.scrollTo({ top: 0, behavior: "instant" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonicalPath, ogImage, ogType, JSON.stringify(jsonLd)]);
  return null;
}

export const orgSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: siteConfig.brand.name,
  url: siteConfig.links.siteDomain,
  sameAs: [siteConfig.links.facebook].filter(Boolean),
  contactPoint: [{
    "@type": "ContactPoint",
    contactType: "sales",
    telephone: siteConfig.scott.phone,
    email: siteConfig.scott.email,
    areaServed: "US-OH",
    availableLanguage: ["en"],
  }],
});

/** Lightweight Person schema for Scott — used on Home, Contact, About. */
export const personSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": `${siteConfig.links.siteDomain}/about-scott#scott`,
  name: siteConfig.scott.fullName,
  jobTitle: "Sales Consultant",
  telephone: siteConfig.scott.phone,
  email: siteConfig.scott.email,
  image: siteConfig.scott.photo,
  url: `${siteConfig.links.siteDomain}/about-scott`,
  worksFor: { "@type": "Organization", name: siteConfig.brand.name },
});

/** Generic WebPage schema. Pass {name, path, description?}. */
export const webPageSchema = ({ name, path, description }) => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${siteConfig.links.siteDomain}${path}#webpage`,
  name,
  url: `${siteConfig.links.siteDomain}${path}`,
  description,
  isPartOf: {
    "@type": "WebSite",
    url: siteConfig.links.siteDomain,
    name: siteConfig.brand.name,
  },
});

/** ContactPage schema for /contact. */
export const contactPageSchema = ({ path = "/contact" }) => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": `${siteConfig.links.siteDomain}${path}#contactpage`,
  url: `${siteConfig.links.siteDomain}${path}`,
  name: `Contact ${siteConfig.scott.fullName}`,
  mainEntity: personSchema(),
});

/** Service schema — for non-inventory help pages (Find Me a Vehicle, Payment Help,
 *  Value My Trade, EV Help, City pages). Provider is a Person, not an AutoDealer:
 *  Scott is the consultant. No dealership name, no address, no LocalBusiness
 *  type — so search engines do not treat city pages as fake local-business
 *  signals. Pricing/offer/vehicle fields are intentionally NOT included; live
 *  inventory comes from vAuto when connected. */
export const serviceSchema = ({ name, path, description, serviceType }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${siteConfig.links.siteDomain}${path}#service`,
  name,
  serviceType: serviceType || name,
  description,
  provider: {
    "@type": "Person",
    name: siteConfig.scott.fullName,
    jobTitle: "Sales Consultant",
    telephone: siteConfig.scott.phone,
    email: siteConfig.scott.email,
    url: `${siteConfig.links.siteDomain}/about-scott`,
  },
  areaServed: { "@type": "State", name: "Ohio" },
  url: `${siteConfig.links.siteDomain}${path}`,
});

/** Article schema — for guide/education pages like EV Help. */
export const articleSchema = ({ headline, path, description, datePublished, dateModified, image }) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline,
  description,
  image: image || `${siteConfig.links.siteDomain}/og-cover.jpg`,
  datePublished: datePublished || "2025-01-01",
  dateModified: dateModified || new Date().toISOString().slice(0, 10),
  author: personSchema(),
  publisher: {
    "@type": "Organization",
    name: siteConfig.brand.name,
    logo: { "@type": "ImageObject", url: siteConfig.logo.url },
  },
  mainEntityOfPage: { "@type": "WebPage", "@id": `${siteConfig.links.siteDomain}${path}` },
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: siteConfig.brand.name,
  url: siteConfig.links.siteDomain,
  potentialAction: {
    "@type": "SearchAction",
    target: `${siteConfig.links.siteDomain}/inventory?search={query}`,
    "query-input": "required name=query",
  },
});

export const breadcrumbs = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    item: `${siteConfig.links.siteDomain}${it.path}`,
  })),
});

export const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const vehicleSchema = (v) => ({
  "@context": "https://schema.org",
  "@type": "Vehicle",
  name: `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim(),
  brand: { "@type": "Brand", name: v.make },
  model: v.model,
  vehicleModelDate: String(v.year),
  vehicleIdentificationNumber: v.vin,
  mileageFromOdometer: v.mileage ? { "@type": "QuantitativeValue", value: v.mileage, unitCode: "SMI" } : undefined,
  bodyType: v.body_style,
  driveWheelConfiguration: v.drivetrain,
  fuelType: v.fuel_type,
  color: v.exterior_color,
  image: (v.photos || []).map((p) => p.url),
  url: `${siteConfig.links.siteDomain}/${v.status}-vehicles/${v.slug}`,
  offers: {
    "@type": "Offer",
    price: v.price,
    priceCurrency: "USD",
    availability: v.availability === "available"
      ? "https://schema.org/InStock"
      : "https://schema.org/OutOfStock",
    seller: { "@type": "Organization", name: siteConfig.brand.name },
    url: `${siteConfig.links.siteDomain}/${v.status}-vehicles/${v.slug}`,
  },
});

/** NOTE: The legacy ServiceAreas-page LocalBusiness schema lives inside
 *  src/pages/ServiceAreas.js so the dealership-identifying constants
 *  (street address, hasMap URL, dealership name) do not ship in chunks
 *  used by privacy-conscious pages like /chevy-sales-*. */

